import React, { FormEvent, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../Icon';
import { initConstellation } from '../landingEffects';
import { toastStore } from '@/store/ToastStore';
import { useStore } from '@store/StoreProvider';

type HeroSectionProps = {
  email: string;
  onEmailChange: (v: string) => void;
};

export const HeroSection: React.FC<HeroSectionProps> = observer(({ email, onEmailChange }) => {
  const heroRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const navigate = useNavigate();
  const { auth, i18n } = useStore();
  const isLoggedIn = auth.isAuthenticated;
  const t = (key: string) => i18n.t(key);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    return initConstellation(canvas, heroRef.current);
  }, []);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toastStore.show(t('errors.auth.enter_email'), 'error');
      return;
    }
    navigate(`/register?email=${encodeURIComponent(email.trim())}`);
  };

  return (
    <section id="hero" ref={heroRef} className="relative min-h-[100svh] flex items-center pt-28 pb-20 overflow-hidden">
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full -z-0" />
      <div className="absolute inset-0 grid-bg -z-0" />

      <div className="relative max-w-5xl mx-auto px-5 sm:px-8 text-center z-10">
        <div data-hero className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brand/30 shimmer-label text-xs sm:text-sm font-semibold text-slate-200 mb-7">
          <Icon id="ic-lock" className="ic ic-sm text-brand2" />
          {t('landing.hero.offshore_privacy')}
        </div>

        <h1 data-hero className="text-[2.6rem] leading-[1.05] sm:text-6xl lg:text-7xl font-extrabold tracking-tight">
          <span className="text-gradient">{t('landing.hero.platform_for')}</span>
          <br className="land-sm-block" />
          <span className="text-white"> {t('landing.hero.with_customers')}</span>
        </h1>

        <p data-hero className="mt-6 text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto">
          {t('landing.hero.tagline')}{' '}
          <span className="text-slate-200 font-semibold">{t('landing.features.no_traces')}</span>
        </p>

        {isLoggedIn ? (
          <div data-hero className="mt-9 flex justify-center">
            <button
              type="button"
              onClick={() => navigate('/myProfile')}
              className="h-[52px] px-8 rounded-2xl bg-brand hover:bg-blue-600 font-semibold text-white whitespace-nowrap shadow-xl shadow-brand/30 transition active:scale-95"
            >
              {t('nav.to_profile')} →
            </button>
          </div>
        ) : (
          <form data-hero onSubmit={handleSubmit} className="mt-9 mx-auto max-w-xl flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Icon id="ic-mail" className="ic ic-sm absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="email"
                placeholder={t('auth.email_label')}
                value={email}
                onChange={(e) => onEmailChange(e.target.value)}
                className="w-full h-[52px] pl-11 pr-4 rounded-2xl bg-panel/80 border border-white/10 focus:border-brand focus:ring-2 focus:ring-brand/40 outline-none placeholder:text-slate-500 transition text-white"
              />
            </div>
            <button type="submit" className="h-[52px] px-7 rounded-2xl bg-brand hover:bg-blue-600 font-semibold text-white whitespace-nowrap shadow-xl shadow-brand/30 transition active:scale-95">
              {t('auth.sign_up')} →
            </button>
          </form>
        )}

        <div data-hero className="mt-7 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-slate-500">
          <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> {t('landing.features.no_logs')}</span>
          <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> {t('billing.methods.summary')}</span>
          <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> {t('billing.features.anti_ddos')}</span>
          <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> {t('landing.pricing.both_channels')}</span>
        </div>
      </div>

      <a href="#immunity" className="absolute bottom-6 left-1/2 -translate-x-1/2 text-slate-500 animate-float land-sm-block" aria-label={t('app.actions.continue')}>
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12l7 7 7-7" /></svg>
      </a>
    </section>
  );
});
